import {
  Button,
  Chip,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { toPageError } from '../partners/PartnerListPage';
import { defaultWalletEntryQuery, walletsRepositoryToken } from './repository';
import type { WalletEntryFilters, WalletEntryQuery, WalletLedgerEntry } from './types';
import { formatWalletMoney } from './types';
import { WalletBalanceChart } from './WalletBalanceChart';

export function WalletLedgerPanel({
  language,
  totalEntryCount,
  walletId,
}: {
  readonly language: TimeLanguage;
  readonly totalEntryCount: string;
  readonly walletId: string;
}) {
  const repository = useRepository(walletsRepositoryToken);
  const { t } = useTranslation();
  const [query, setQuery] = useState(defaultWalletEntryQuery);
  const [cursorHistory, setCursorHistory] = useState<readonly string[]>(['']);
  const [pageIndex, setPageIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.listEntries(walletId, query, signal),
    [query, refreshKey, repository, walletId],
    (page) => page.items.length === 0 && query.cursor === '',
  );

  const apply = (filters: WalletEntryFilters, sort: WalletEntryQuery['sort']) => {
    setPageIndex(0);
    setCursorHistory(['']);
    setQuery({ ...query, cursor: '', filters, sort });
  };

  return (
    <Stack spacing={2}>
      <WalletEntryFiltersForm onApply={apply} value={query} />
      <PageState
        emptyMessage={t('wallets.ledger.empty')}
        error={toPageError(resource.error, t('wallets.errors.entries'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={1}>
            <LedgerResults entries={resource.data.items} language={language} />
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Button
                disabled={pageIndex === 0}
                onClick={() => {
                  const previous = pageIndex - 1;
                  setPageIndex(previous);
                  setQuery((current) => ({
                    ...current,
                    cursor: cursorHistory[previous] ?? '',
                  }));
                }}
              >
                {t('wallets.pagination.previous')}
              </Button>
              <Button
                disabled={resource.data.nextCursor === null}
                onClick={() => {
                  const cursor = resource.data?.nextCursor;
                  if (cursor === null || cursor === undefined) return;
                  const next = pageIndex + 1;
                  setCursorHistory((current) => [...current.slice(0, next), cursor]);
                  setPageIndex(next);
                  setQuery((current) => ({ ...current, cursor }));
                }}
              >
                {t('wallets.pagination.next')}
              </Button>
            </Stack>
            <WalletBalanceChart
              entries={resource.data.items}
              language={language}
              totalEntryCount={totalEntryCount}
            />
          </Stack>
        )}
      </PageState>
    </Stack>
  );
}

function WalletEntryFiltersForm({
  onApply,
  value,
}: {
  readonly onApply: (filters: WalletEntryFilters, sort: WalletEntryQuery['sort']) => void;
  readonly value: WalletEntryQuery;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<
    WalletEntryFilters & { readonly sort: WalletEntryQuery['sort'] }
  >({ defaultValues: { ...value.filters, sort: value.sort } });
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      sourceWithdrawalId: value.filters.sourceWithdrawalId,
      sourceAdjustmentId: value.filters.sourceAdjustmentId,
      minimumAmount: value.filters.minimumAmount,
      maximumAmount: value.filters.maximumAmount,
      sort: value.sort,
    },
    {
      sourceWithdrawalId: defaultWalletEntryQuery.filters.sourceWithdrawalId,
      sourceAdjustmentId: defaultWalletEntryQuery.filters.sourceAdjustmentId,
      minimumAmount: defaultWalletEntryQuery.filters.minimumAmount,
      maximumAmount: defaultWalletEntryQuery.filters.maximumAmount,
      sort: defaultWalletEntryQuery.sort,
    },
  );
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      applyLabel={t('wallets.filters.apply')}
      ariaLabel={t('filterPanel.label', { page: t('wallets.detail.ledger') })}
      onClear={() => {
        reset({ ...defaultWalletEntryQuery.filters, sort: defaultWalletEntryQuery.sort });
        onApply(defaultWalletEntryQuery.filters, defaultWalletEntryQuery.sort);
      }}
      onSubmit={handleSubmit(({ sort, ...filters }) =>
        onApply(normalizeEntryFilters(filters), sort),
      )}
      primaryFields={
        <>
          <TextField
            defaultValue=""
            label={t('wallets.fields.entryType')}
            select
            size="small"
            {...register('entryType')}
          >
            <MenuItem value="">{t('wallets.filters.all')}</MenuItem>
            {[
              'AFFILIATE_EARN',
              'AFFILIATE_REVERSAL',
              'ADJUSTMENT',
              'WITHDRAWAL_RESERVE',
              'WITHDRAWAL_COMPLETE',
              'WITHDRAWAL_RELEASE',
            ].map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('wallets.fields.transactionId')}
            size="small"
            {...register('sourceTransactionId')}
          />
          <TextField
            label={t('wallets.filters.effectiveFrom')}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            type="datetime-local"
            {...register('effectiveFrom')}
          />
          <TextField
            label={t('wallets.filters.effectiveTo')}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            type="datetime-local"
            {...register('effectiveTo')}
          />
        </>
      }
      secondaryFields={
        <>
          <TextField
            label={t('wallets.fields.withdrawalId')}
            size="small"
            {...register('sourceWithdrawalId')}
          />
          <TextField
            label={t('wallets.fields.adjustmentId')}
            size="small"
            {...register('sourceAdjustmentId')}
          />
          <TextField
            label={t('wallets.filters.minimumAmount')}
            size="small"
            {...register('minimumAmount')}
          />
          <TextField
            label={t('wallets.filters.maximumAmount')}
            size="small"
            {...register('maximumAmount')}
          />
          <TextField
            defaultValue={defaultWalletEntryQuery.sort}
            label={t('wallets.filters.sort')}
            select
            size="small"
            {...register('sort')}
          >
            <MenuItem value="effectiveAt">{t('wallets.fields.effectiveAt')}</MenuItem>
            <MenuItem value="createdAt">{t('wallets.fields.createdAt')}</MenuItem>
          </TextField>
        </>
      }
    />
  );
}

function LedgerResults({
  entries,
  language,
}: {
  readonly entries: readonly WalletLedgerEntry[];
  readonly language: TimeLanguage;
}) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const auth = useAuth();
  const permissions = new Set(auth.status === 'authenticated' ? auth.principal.permissions : []);
  const { t } = useTranslation();
  if (mobile) {
    return (
      <Stack
        aria-label={t('wallets.ledger.results')}
        component="ul"
        spacing={1}
        sx={{ listStyle: 'none', m: 0, p: 0 }}
      >
        {entries.map((entry) => (
          <Paper component="li" key={entry.id} sx={{ p: 2 }} variant="outlined">
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1}>
                <Chip label={entry.entryType} size="small" />
                <Typography sx={{ fontWeight: 700 }}>
                  {formatWalletMoney(entry.amount, entry.currencyCode, entry.currencyMinorUnit)}
                </Typography>
              </Stack>
              <Typography variant="body2">{entry.id}</Typography>
              <Typography variant="body2">
                {t('wallets.fields.availableAfter')}:{' '}
                {formatWalletMoney(
                  entry.availableAfter,
                  entry.currencyCode,
                  entry.currencyMinorUnit,
                )}
              </Typography>
              <SourceLinks entry={entry} permissions={permissions} />
              <Typography color="text.secondary" variant="caption">
                {formatWithOffset(entry.effectiveAt, language)}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }
  return (
    <TableContainer
      aria-label={t('wallets.ledger.results')}
      component={Paper}
      sx={{ maxWidth: '100%', overflowX: 'auto' }}
      tabIndex={0}
      variant="outlined"
    >
      <Table size="small" sx={{ minWidth: 1800 }}>
        <TableHead>
          <TableRow>
            {[
              'entryId',
              'entryType',
              'amount',
              'availableBefore',
              'availableDelta',
              'availableAfter',
              'reservedBefore',
              'reservedDelta',
              'reservedAfter',
              'sources',
              'effectiveAt',
            ].map((field) => (
              <TableCell key={field}>{t(`wallets.fields.${field}`)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell>{entry.id}</TableCell>
              <TableCell>
                <Chip label={entry.entryType} size="small" />
              </TableCell>
              {[
                entry.amount,
                entry.availableBefore,
                entry.availableDelta,
                entry.availableAfter,
                entry.reservedBefore,
                entry.reservedDelta,
                entry.reservedAfter,
              ].map((amount, index) => (
                <TableCell key={index}>
                  {formatWalletMoney(amount, entry.currencyCode, entry.currencyMinorUnit)}
                </TableCell>
              ))}
              <TableCell>
                <SourceLinks entry={entry} permissions={permissions} />
              </TableCell>
              <TableCell>{formatWithOffset(entry.effectiveAt, language)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SourceLinks({
  entry,
  permissions,
}: {
  readonly entry: WalletLedgerEntry;
  readonly permissions: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const sources = [
    {
      id: entry.source.transactionId,
      permission: 'TRANSACTION_VIEW',
      label: t('wallets.links.transaction'),
      path: '/transactions',
    },
    {
      id: entry.source.withdrawalId,
      permission: 'WITHDRAWAL_VIEW',
      label: t('wallets.links.withdrawal'),
      path: '/affiliate-withdrawals',
    },
    {
      id: entry.source.commissionLedgerEntryId,
      permission: 'COMMISSION_LEDGER_VIEW',
      label: t('wallets.links.commissionLedger'),
      path: '/commission-ledger',
    },
    {
      id: entry.source.walletAdjustmentId,
      permission: 'WALLET_VIEW',
      label: t('wallets.links.walletAdjustment'),
      path: null,
    },
  ];
  return (
    <Stack spacing={0.25}>
      {sources
        .filter((source) => source.id !== null)
        .map((source) =>
          permissions.has(source.permission) && source.path !== null ? (
            <MuiLink component={Link} key={source.label} to={`${source.path}/${source.id}`}>
              {source.label} · {source.id}
            </MuiLink>
          ) : (
            <Typography key={source.label} variant="body2">
              {source.id}
            </Typography>
          ),
        )}
    </Stack>
  );
}

function normalizeEntryFilters(filters: WalletEntryFilters): WalletEntryFilters {
  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [key, value.trim()]),
  ) as unknown as WalletEntryFilters;
}

function formatWithOffset(timestamp: string, language: TimeLanguage) {
  const formatted = formatTimestamp(timestamp, 'Asia/Bangkok', language);
  return `${formatted.display} (${formatted.originalOffset})`;
}
